import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  PaperProps,
  Popover,
  Portal,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { DateCalendar } from "@mui/x-date-pickers";
import Draggable from "react-draggable";
import dayjs, { Dayjs } from "dayjs";
import { t } from "i18next";

interface DueDateTimelineProps {
  dueDate: string | Date | null | undefined;
  previousDueDates?: Array<string | Date | null | undefined>;
  fromDays: number;
  toDays: number;
  showDot?: boolean;
  onDueDateChange?: (newDate: string) => void;
}

const HOLD_MS = 200;

const PAST_ACTIVE = "#F23D47";
const PAST_RAIL = "#EAB9BB";
const FUTURE_ACTIVE = "#242C63";
const FUTURE_RAIL = "#7C8AC5";
const PREVIOUS_DOT = "#B6BAC2";

const TEXT_PRIMARY = "#1A2048";
const DESTRUCTIVE = "#A92A2A";
const BORDER = "#E5E7EB";
const SOFT_SURFACE = "#F8FAFC";
const FONT_FAMILY = '"Inter Tight", "Inter", system-ui, sans-serif';

function DraggablePaper(props: PaperProps) {
  const nodeRef = useRef<HTMLDivElement>(null);
  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      handle="#due-date-confirm-dialog-title"
      cancel={
        '[class*="MuiDialogContent-root"], [class*="MuiDialogActions-root"], button'
      }
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

const DueDateTimeline: React.FC<DueDateTimelineProps> = ({
  dueDate,
  previousDueDates,
  fromDays,
  toDays,
  showDot = true,
  onDueDateChange,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);

  // Visible percent position of the dot while armed/dragging. Null = not overridden (use original).
  const [draggedPercent, setDraggedPercent] = useState<number | null>(null);
  const [armed, setArmed] = useState<boolean>(false);

  // Cached tooltip anchor position (computed from track rect) to avoid
  // reading getBoundingClientRect() inline in JSX during drag renders.
  const [tooltipAnchor, setTooltipAnchor] = useState<{
    left: number;
    top: number;
  } | null>(null);

  // Confirmation dialog state
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [confirmFrom, setConfirmFrom] = useState<string>("");
  const [confirmTo, setConfirmTo] = useState<string>("");

  // Popover for new-date picker inside dialog
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);

  // Refs holding live drag state so we can avoid re-renders during movement.
  const holdTimerRef = useRef<number | null>(null);
  const armedRef = useRef<boolean>(false);
  const movedRef = useRef<boolean>(false);
  const originalDayOffsetRef = useRef<number | null>(null);
  const currentDayOffsetRef = useRef<number | null>(null);
  const startClientXRef = useRef<number>(0);
  // Snapshot of the ISO due-date taken at beginHold, so handlePointerUp's
  // ISO math is not affected by a stale `due` captured in the closure.
  const originalDueISORef = useRef<string>("");
  // Live dragged percent mirrored as a ref so updateTooltipAnchor can read
  // the freshest value without waiting for state commit.
  const draggedPercentRef = useRef<number | null>(null);
  // "Latest" refs for global listener callbacks, so window listeners can
  // always dispatch into the most recent closure regardless of re-renders.
  const pointerUpRef = useRef<((e: MouseEvent | TouchEvent) => void) | null>(
    null,
  );
  const pointerMoveRef = useRef<((clientX: number) => void) | null>(null);
  // Cleanup for attached global drag listeners, so we can detach on unmount.
  const cleanupDragListenersRef = useRef<null | (() => void)>(null);

  // --- derived values --------------------------------------------------------

  const range = toDays - fromDays;
  const hasDueDate =
    dueDate !== null && dueDate !== undefined && dueDate !== "";
  const today = dayjs().startOf("day");
  const due = hasDueDate ? dayjs(dueDate).startOf("day") : null;
  const originalOffsetDays = due ? due.diff(today, "day") : null;

  const midpointPercent = range !== 0 ? ((0 - fromDays) / range) * 100 : 50;

  const originalPercent =
    range !== 0 && originalOffsetDays !== null
      ? ((originalOffsetDays - fromDays) / range) * 100
      : 50;

  const clampVisual = (pct: number) => Math.max(2, Math.min(98, pct));

  const effectivePercent =
    draggedPercent !== null ? draggedPercent : originalPercent;
  const clampedPosition = clampVisual(effectivePercent);

  // For coloring the dot: during drag, use the dragged day offset; else, original.
  const effectiveDayOffset =
    draggedPercent !== null && range !== 0
      ? Math.round(fromDays + (draggedPercent / 100) * range)
      : originalOffsetDays;
  const isPast = effectiveDayOffset !== null ? effectiveDayOffset < 0 : false;
  const dotColor = isPast ? PAST_ACTIVE : FUTURE_ACTIVE;

  const dragEnabled = !!onDueDateChange && hasDueDate && showDot && range !== 0;

  // Compute visible positions for previous due-date dots. De-duplicated by
  // ISO day, clipped to current range, and we skip any entry that coincides
  // with the active due date so the grey dot does not sit under the colored one.
  const todayISO = today.format("YYYY-MM-DD");
  const dueISO = due ? due.format("YYYY-MM-DD") : "";
  const previousDotKey = useMemo(() => {
    if (!previousDueDates || previousDueDates.length === 0) return "";
    return previousDueDates
      .map((d) => {
        if (!d) return "";
        const parsed = dayjs(d);
        return parsed.isValid() ? parsed.toISOString() : "";
      })
      .join("|");
  }, [previousDueDates]);
  const previousDots = useMemo(() => {
    if (!previousDueDates || previousDueDates.length === 0 || range === 0)
      return [] as { key: string; percent: number; label: string }[];
    const seen = new Set<string>();
    const todayRef = dayjs(todayISO).startOf("day");
    const out: { key: string; percent: number; label: string }[] = [];
    for (const raw of previousDueDates) {
      if (!raw) continue;
      const day = dayjs(raw).startOf("day");
      if (!day.isValid()) continue;
      const iso = day.format("YYYY-MM-DD");
      if (seen.has(iso)) continue;
      seen.add(iso);
      if (dueISO && iso === dueISO) continue;
      const offset = day.diff(todayRef, "day");
      if (offset < fromDays || offset > toDays) continue;
      const percent = ((offset - fromDays) / range) * 100;
      out.push({
        key: iso,
        percent: Math.max(2, Math.min(98, percent)),
        label: day.format("D MMM YYYY"),
      });
    }
    return out;
  }, [previousDotKey, todayISO, dueISO, fromDays, toDays, range]);

  // --- formatting helpers ----------------------------------------------------

  const formatOffset = useCallback((days: number) => {
    if (days > 0) {
      return `+${days} ${t(
        days === 1 ? "dueDateTimeline.dayLater" : "dueDateTimeline.daysLater",
      )}`;
    }
    const abs = Math.abs(days);
    return `${abs} ${t(
      abs === 1 ? "dueDateTimeline.dayEarlier" : "dueDateTimeline.daysEarlier",
    )}`;
  }, []);

  // --- drag math -------------------------------------------------------------

  const computeDayOffsetFromClientX = useCallback(
    (clientX: number): number => {
      const track = trackRef.current;
      if (!track || range === 0) return originalOffsetDays ?? 0;
      const rect = track.getBoundingClientRect();
      if (rect.width === 0) return originalOffsetDays ?? 0;
      const raw = fromDays + ((clientX - rect.left) / rect.width) * range;
      return Math.max(fromDays, Math.min(toDays, Math.round(raw)));
    },
    [fromDays, toDays, range, originalOffsetDays],
  );

  const dayOffsetToPercent = useCallback(
    (dayOffset: number): number => {
      if (range === 0) return 50;
      return ((dayOffset - fromDays) / range) * 100;
    },
    [fromDays, range],
  );

  // Cache the tooltip's anchor position (track rect + current percent) so
  // JSX doesn't call getBoundingClientRect() inline on every render.
  const updateTooltipAnchor = useCallback(() => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const percent =
      draggedPercentRef.current !== null
        ? draggedPercentRef.current
        : originalPercent;
    const visualPercent = clampVisual(percent);
    setTooltipAnchor({
      left: rect.left + (rect.width * visualPercent) / 100,
      top: rect.top,
    });
  }, [originalPercent]);

  // --- drag lifecycle --------------------------------------------------------

  const clearHoldTimer = useCallback(() => {
    if (holdTimerRef.current !== null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const resetDragState = useCallback(() => {
    clearHoldTimer();
    armedRef.current = false;
    movedRef.current = false;
    originalDayOffsetRef.current = null;
    currentDayOffsetRef.current = null;
    draggedPercentRef.current = null;
    setArmed(false);
    setDraggedPercent(null);
    setTooltipAnchor(null);
  }, [clearHoldTimer]);

  const handlePointerMove = useCallback(
    (clientX: number) => {
      if (!armedRef.current) return;
      const newOffset = computeDayOffsetFromClientX(clientX);
      if (
        originalDayOffsetRef.current !== null &&
        newOffset !== originalDayOffsetRef.current
      ) {
        movedRef.current = true;
      }
      currentDayOffsetRef.current = newOffset;
      const newPercent = dayOffsetToPercent(newOffset);
      setDraggedPercent(newPercent);
      draggedPercentRef.current = newPercent;
      updateTooltipAnchor();
    },
    [computeDayOffsetFromClientX, dayOffsetToPercent, updateTooltipAnchor],
  );

  const handlePointerUp = useCallback(() => {
    // Detach listeners first; we do this in the effect cleanup via `armedRef` flip.
    const wasArmed = armedRef.current;
    const finalOffset = currentDayOffsetRef.current;
    const origOffset = originalDayOffsetRef.current;

    clearHoldTimer();
    armedRef.current = false;
    setArmed(false);

    if (!wasArmed) {
      // Released before arming -> cancel cleanly.
      resetDragState();
      return;
    }

    // Armed. Check for actual day shift.
    if (
      finalOffset !== null &&
      origOffset !== null &&
      finalOffset !== origOffset &&
      originalDueISORef.current
    ) {
      // Open confirmation dialog. Keep draggedPercent so dot stays put.
      // Use the ISO snapshot taken at beginHold to avoid stale-closure math
      // when the parent has optimistically updated `dueDate`.
      const fromISO = originalDueISORef.current;
      const toISO = dayjs(fromISO)
        .add(finalOffset - origOffset, "day")
        .format("YYYY-MM-DD");
      setConfirmFrom(fromISO);
      setConfirmTo(toISO);
      setDialogOpen(true);
    } else {
      // Armed but no actual movement -> snap back.
      setDraggedPercent(null);
      draggedPercentRef.current = null;
      setTooltipAnchor(null);
    }

    movedRef.current = false;
    originalDayOffsetRef.current = null;
    currentDayOffsetRef.current = null;
  }, [clearHoldTimer, resetDragState]);

  // If `dragEnabled` flips false mid-drag (e.g. parent removed the date),
  // fully tear down the drag session so we don't leave the tooltip armed
  // and listeners dangling. The general unmount cleanup effect handles
  // component teardown; this one handles the live-flip case.
  useEffect(() => {
    if (!dragEnabled && armedRef.current) {
      resetDragState();
      cleanupDragListenersRef.current?.();
      cleanupDragListenersRef.current = null;
    }
  }, [dragEnabled, resetDragState]);

  const attachGlobalMouseListeners = useCallback(() => {
    const onMove = (e: MouseEvent) => pointerMoveRef.current?.(e.clientX);
    const detach = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    const onUp = (e: MouseEvent) => {
      detach();
      cleanupDragListenersRef.current = null;
      pointerUpRef.current?.(e);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    cleanupDragListenersRef.current = detach;
  }, []);

  const attachGlobalTouchListeners = useCallback(() => {
    const onMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        pointerMoveRef.current?.(e.touches[0].clientX);
      }
    };
    const detach = () => {
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
    const onEnd = (e: TouchEvent) => {
      detach();
      cleanupDragListenersRef.current = null;
      pointerUpRef.current?.(e);
    };
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", onEnd);
    cleanupDragListenersRef.current = detach;
  }, []);

  // Unmount cleanup: cancel any pending hold timer and detach any active
  // global drag listeners so we don't setState on an unmounted component.
  useEffect(() => {
    return () => {
      if (holdTimerRef.current !== null) {
        window.clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
      if (cleanupDragListenersRef.current) {
        cleanupDragListenersRef.current();
        cleanupDragListenersRef.current = null;
      }
    };
  }, []);

  // Keep the "latest" refs for global-listener dispatch pointed at the most
  // recent callback closures, so window listeners never call into stale
  // state after a re-render (e.g. parent optimistic update).
  useEffect(() => {
    pointerUpRef.current = handlePointerUp;
    pointerMoveRef.current = handlePointerMove;
  });

  // While armed, refresh the cached tooltip anchor on scroll/resize so the
  // Portal-rendered tooltip follows the track if the user scrolls the page.
  useEffect(() => {
    if (!armed) return;
    const onScrollOrResize = () => updateTooltipAnchor();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [armed, updateTooltipAnchor]);

  const beginHold = useCallback(
    (clientX: number) => {
      if (!dragEnabled || originalOffsetDays === null || !due) return;
      startClientXRef.current = clientX;
      originalDayOffsetRef.current = originalOffsetDays;
      currentDayOffsetRef.current = originalOffsetDays;
      // Snapshot the ISO due-date at the moment the hold starts; this is
      // what handlePointerUp uses to compute `fromISO`/`toISO`, immune to
      // `due` being recomputed on re-renders that happen mid-interaction.
      originalDueISORef.current = due.format("YYYY-MM-DD");
      movedRef.current = false;
      clearHoldTimer();
      holdTimerRef.current = window.setTimeout(() => {
        armedRef.current = true;
        setArmed(true);
        // Snap the dragged percent to the original position immediately upon arming
        // so the tooltip appears above the dot.
        const startPercent = dayOffsetToPercent(originalOffsetDays);
        setDraggedPercent(startPercent);
        draggedPercentRef.current = startPercent;
        // Prime the cached tooltip anchor so the Portal can render without
        // reading getBoundingClientRect() inline in JSX.
        updateTooltipAnchor();
      }, HOLD_MS);
    },
    [
      clearHoldTimer,
      dayOffsetToPercent,
      dragEnabled,
      due,
      originalOffsetDays,
      updateTooltipAnchor,
    ],
  );

  const handleDotMouseDown = (e: React.MouseEvent) => {
    if (!dragEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    beginHold(e.clientX);
    attachGlobalMouseListeners();
  };

  const handleDotTouchStart = (e: React.TouchEvent) => {
    if (!dragEnabled) return;
    if (e.touches.length === 0) return;
    beginHold(e.touches[0].clientX);
    attachGlobalTouchListeners();
  };

  // --- dialog handlers -------------------------------------------------------

  const handleDialogClose = () => {
    setDialogOpen(false);
    setPickerAnchor(null);
    setConfirmFrom("");
    setConfirmTo("");
    resetDragState();
  };

  const handleCancel = () => {
    handleDialogClose();
  };

  const handleConfirm = () => {
    if (onDueDateChange && confirmTo) {
      onDueDateChange(confirmTo);
    }
    setDialogOpen(false);
    setPickerAnchor(null);
    setConfirmFrom("");
    setConfirmTo("");
    resetDragState();
  };

  const handleNewDateRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setPickerAnchor(e.currentTarget);
  };

  const handlePickerClose = () => {
    setPickerAnchor(null);
  };

  const handlePickerChange = (newValue: Dayjs | null) => {
    if (newValue && newValue.isValid()) {
      setConfirmTo(newValue.format("YYYY-MM-DD"));
    }
    setPickerAnchor(null);
  };

  // --- render ----------------------------------------------------------------

  if (range === 0) return <Box sx={{ height: 24 }} />;

  const showDotNow = showDot && hasDueDate;

  const shiftDays =
    confirmFrom && confirmTo
      ? dayjs(confirmTo).diff(dayjs(confirmFrom), "day")
      : 0;
  const shiftColor = shiftDays >= 0 ? FUTURE_ACTIVE : DESTRUCTIVE;

  // Derive tooltip values from render state (`draggedPercent`) rather than
  // mutable refs. This removes fragile render-ordering coupling and the
  // need to guard against null refs with `?? 0` arithmetic.
  const liveOffset =
    draggedPercent !== null && range !== 0
      ? Math.round(fromDays + (draggedPercent / 100) * range)
      : null;

  const tooltipTargetDate =
    armed && liveOffset !== null && originalOffsetDays !== null && due
      ? due.add(liveOffset - originalOffsetDays, "day")
      : null;

  const tooltipShiftDays =
    armed && liveOffset !== null && originalOffsetDays !== null
      ? liveOffset - originalOffsetDays
      : 0;

  return (
    <>
      <Box
        ref={trackRef}
        sx={{
          position: "relative",
          height: 24,
          width: "100%",
          minWidth: 120,
          userSelect: "none",
          touchAction: "none",
        }}
      >
        {/* Track background */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: 2,
            transform: "translateY(-50%)",
            borderRadius: 999,
            backgroundColor: "#e0e0e0",
          }}
        />

        {/* Past section (red tint) */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: 0,
            height: 2,
            transform: "translateY(-50%)",
            width: `${Math.max(0, Math.min(100, midpointPercent))}%`,
            backgroundColor: PAST_RAIL,
          }}
        />

        {/* Future section (blue tint) */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: 0,
            height: 2,
            transform: "translateY(-50%)",
            left: `${Math.max(0, Math.min(100, midpointPercent))}%`,
            backgroundColor: FUTURE_RAIL,
          }}
        />

        {/* Midpoint marker (today) */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            width: "1px",
            height: 12,
            transform: "translateY(-50%)",
            left: `${Math.max(0, Math.min(100, midpointPercent))}%`,
            backgroundColor: "#999",
          }}
        />

        {/* Previous due-date dots (grey, non-interactive) */}
        {previousDots.map((d) => (
          <Tooltip
            key={d.key}
            title={`${t("dueDateTimeline.previous")}: ${d.label}`}
            arrow
            placement="top"
            slotProps={{ popper: { sx: { zIndex: 1900 } } }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translate(-50%, -50%)",
                borderRadius: "50%",
                left: `${d.percent}%`,
                width: 8,
                height: 8,
                backgroundColor: PREVIOUS_DOT,
                opacity: 0.85,
                pointerEvents: "auto",
                zIndex: 1,
              }}
            />
          </Tooltip>
        ))}

        {/* Dot */}
        {showDotNow && (
          <Tooltip
            title={due ? due.format("D MMM YYYY") : ""}
            arrow
            placement="top"
            disableHoverListener={armed}
            disableFocusListener={armed}
            disableTouchListener={armed}
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
          >
            <Box
              onMouseDown={handleDotMouseDown}
              onTouchStart={handleDotTouchStart}
              sx={[{
                position: "absolute",
                top: "50%",
                transform: "translate(-50%, -50%)",
                borderRadius: "50%",
                left: `${clampedPosition}%`,
                backgroundColor: dotColor,

                transition:
                  "width 120ms ease, height 120ms ease, box-shadow 120ms ease",

                zIndex: 2
              }, armed ? {
                width: 14
              } : {
                width: 10
              }, armed ? {
                height: 14
              } : {
                height: 10
              }, armed ? {
                boxShadow: `0 0 0 4px rgba(36, 44, 99, 0.18)`
              } : {
                boxShadow: `0 0 4px ${dotColor}40`
              }, dragEnabled ? {
                cursor: (armed ? "grabbing" : "grab")
              } : {
                cursor: "default"
              }]}
            />
          </Tooltip>
        )}

        {/* Floating tooltip while armed — rendered to body via Portal so row stacking context can't clip it */}
        {armed && showDotNow && tooltipTargetDate && tooltipAnchor && (
          <Portal>
            <Box
              sx={{
                position: "fixed",
                left: `${tooltipAnchor.left}px`,
                top: `${tooltipAnchor.top}px`,
                transform: "translate(-50%, -100%) translateY(-8px)",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                backgroundColor: "#ffffff",
                border: `1px solid ${BORDER}`,
                borderRadius: "6px",
                boxShadow: "0 2px 8px rgba(36,44,99,0.12)",
                fontFamily: FONT_FAMILY,
                px: 1.25,
                py: 0.75,
                zIndex: 2000,
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: "50%",
                  bottom: -4,
                  width: 8,
                  height: 8,
                  backgroundColor: "#ffffff",
                  borderRight: `1px solid ${BORDER}`,
                  borderBottom: `1px solid ${BORDER}`,
                  transform: "translateX(-50%) rotate(45deg)",
                },
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: TEXT_PRIMARY,
                  lineHeight: 1.3,
                }}
              >
                {tooltipTargetDate.format("D MMM YYYY")}
              </Typography>
              <Typography
                component="div"
                sx={[{
                  fontFamily: FONT_FAMILY,
                  fontSize: "0.7rem",
                  lineHeight: 1.3
                }, tooltipShiftDays >= 0 ? {
                  color: FUTURE_ACTIVE
                } : {
                  color: DESTRUCTIVE
                }]}
              >
                {tooltipShiftDays === 0 ? "—" : formatOffset(tooltipShiftDays)}
              </Typography>
            </Box>
          </Portal>
        )}
      </Box>
      {/* Confirmation dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        PaperComponent={DraggablePaper}
        aria-labelledby="due-date-confirm-dialog-title"
        maxWidth="xs"
        fullWidth slotProps={{ paper: {
          sx: {
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow: "0px 16px 40px -8px rgba(36, 44, 99, 0.12)",
          },
        } }}
      >
        <DialogTitle
          id="due-date-confirm-dialog-title"
          sx={{
            cursor: "move",
            userSelect: "none",
            fontFamily: FONT_FAMILY,
            color: TEXT_PRIMARY,
            borderBottom: `1px solid ${BORDER}`,
            py: 1.5,
            px: 3,
            fontSize: "1rem",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box component="span">{t("dueDateTimeline.confirmTitle")}</Box>
          <IconButton
            size="small"
            aria-label={t("dueDateTimeline.cancel") as string}
            onClick={handleDialogClose}
            sx={{ color: TEXT_PRIMARY }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <Typography
            component="div"
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: "0.8125rem",
              color: "text.secondary",
              mb: 2,
              mt: 2,
            }}
          >
            {t("dueDateTimeline.confirmMessage")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {/* Previous date */}
            <Box
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: "6px",
                px: 1.5,
                py: 1,
                fontFamily: FONT_FAMILY,
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: "0.7rem",
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {t("dueDateTimeline.previous")}
              </Typography>
              <Typography
                component="div"
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: "0.95rem",
                  color: TEXT_PRIMARY,
                }}
              >
                {confirmFrom ? dayjs(confirmFrom).format("D MMM YYYY") : ""}
              </Typography>
            </Box>

            {/* Shift: Divider + Typography + Divider */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1,
                fontFamily: FONT_FAMILY,
              }}
            >
              <Divider sx={{ flex: 1, borderColor: BORDER }} />
              <Typography
                component="div"
                sx={{
                  fontFamily: FONT_FAMILY,
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: shiftColor,
                  whiteSpace: "nowrap",
                }}
              >
                {shiftDays === 0
                  ? t("dueDateTimeline.shift")
                  : `${t("dueDateTimeline.shift")}: ${formatOffset(shiftDays)}`}
              </Typography>
              <Divider sx={{ flex: 1, borderColor: BORDER }} />
            </Box>

            {/* New date (interactive) */}
            <Box
              role="button"
              tabIndex={0}
              onClick={handleNewDateRowClick}
              sx={{
                border: `1px solid ${BORDER}`,
                borderRadius: "6px",
                px: 1.5,
                py: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                fontFamily: FONT_FAMILY,
                "&:hover": {
                  backgroundColor: SOFT_SURFACE,
                },
              }}
            >
              <Box>
                <Typography
                  component="div"
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontSize: "0.7rem",
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("dueDateTimeline.newDate")}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    fontFamily: FONT_FAMILY,
                    fontSize: "0.95rem",
                    color: TEXT_PRIMARY,
                  }}
                >
                  {confirmTo ? dayjs(confirmTo).format("D MMM YYYY") : ""}
                </Typography>
              </Box>
              <CalendarTodayOutlinedIcon
                aria-hidden="true"
                sx={{
                  fontSize: 18,
                  color: "text.secondary",
                  flexShrink: 0,
                }}
              />
            </Box>
          </Box>

          <Popover
            open={Boolean(pickerAnchor)}
            anchorEl={pickerAnchor}
            onClose={handlePickerClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <DateCalendar
              value={confirmTo ? dayjs(confirmTo) : dayjs()}
              onChange={handlePickerChange}
            />
          </Popover>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: `1px solid ${BORDER}`, gap: 1 }}
        >
          <Button
            onClick={handleCancel}
            variant="outlined"
            sx={{
              fontFamily: FONT_FAMILY,
              textTransform: "none",
              borderColor: BORDER,
              color: TEXT_PRIMARY,
              boxShadow: "none",
              "&:hover": { borderColor: "#C3C7CC", boxShadow: "none" },
            }}
          >
            {t("dueDateTimeline.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={shiftDays === 0}
            variant="contained"
            sx={{
              fontFamily: FONT_FAMILY,
              textTransform: "none",
              backgroundColor: FUTURE_ACTIVE,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#353D7A", boxShadow: "none" },
            }}
          >
            {t("dueDateTimeline.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DueDateTimeline;
